import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addTimeOffThunk,
  removeTimeOffThunk,
  addSpecialHoursThunk,
} from "../../../../store/thunks/adminThunks";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Input from "../../../../components/common/Input";
import Dropdown from "../../../../components/common/Dropdown";
import Checkbox from "../../../../components/common/Checkbox";
import Modal from "../../../../components/common/Modal";
import {
  CalendarIcon,
  PlusIcon,
  TrashIcon,
  ClockIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/outline";

const TimeOffCalendar = ({ timeOffList, specialHoursList, admin }) => {
  const dispatch = useDispatch();
  const { loading, timeOffTypes } = useSelector((state) => state.admin);

  // Local state
  const [activeTab, setActiveTab] = useState("time-off");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSpecialHoursModalOpen, setIsSpecialHoursModalOpen] = useState(false);

  // Time off form state
  const [timeOffForm, setTimeOffForm] = useState({
    date: "",
    type: null,
    reason: "",
    isFullDay: true,
    startTime: "",
    endTime: "",
  });

  // Special hours form state
  const [specialHoursForm, setSpecialHoursForm] = useState({
    date: "",
    isAvailable: true,
    startTime: "",
    endTime: "",
    reason: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Time off type options
  const timeOffTypeOptions = (
    timeOffTypes || [
      { value: "VACATION", label: "Vacation" },
      { value: "SICK_LEAVE", label: "Sick Leave" },
      { value: "PERSONAL", label: "Personal Day" },
      { value: "TRAINING", label: "Training" },
      { value: "OTHER", label: "Other" },
    ]
  ).map((type) => ({
    _id: type.value,
    name: type.label,
  }));

  // Get type color
  const getTypeColor = (type) => {
    const colors = {
      VACATION: "info",
      SICK_LEAVE: "danger",
      PERSONAL: "warning",
      TRAINING: "success",
      OTHER: "default",
    };
    return colors[type] || "default";
  };

  // Reset time off form
  const resetTimeOffForm = () => {
    setTimeOffForm({
      date: "",
      type: null,
      reason: "",
      isFullDay: true,
      startTime: "",
      endTime: "",
    });
    setFormErrors({});
  };

  // Reset special hours form
  const resetSpecialHoursForm = () => {
    setSpecialHoursForm({
      date: "",
      isAvailable: true,
      startTime: "",
      endTime: "",
      reason: "",
    });
    setFormErrors({});
  };

  // Validate time off form
  const validateTimeOffForm = () => {
    const errors = {};

    if (!timeOffForm.date) {
      errors.date = "Date is required";
    }

    if (!timeOffForm.type) {
      errors.type = "Type is required";
    }

    if (!timeOffForm.isFullDay) {
      if (!timeOffForm.startTime) {
        errors.startTime = "Start time is required for partial day";
      }
      if (!timeOffForm.endTime) {
        errors.endTime = "End time is required for partial day";
      }
      if (timeOffForm.startTime && timeOffForm.endTime) {
        if (timeOffForm.endTime <= timeOffForm.startTime) {
          errors.endTime = "End time must be after start time";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate special hours form
  const validateSpecialHoursForm = () => {
    const errors = {};

    if (!specialHoursForm.date) {
      errors.date = "Date is required";
    }

    if (specialHoursForm.isAvailable) {
      if (!specialHoursForm.startTime) {
        errors.startTime = "Start time is required";
      }
      if (!specialHoursForm.endTime) {
        errors.endTime = "End time is required";
      }
      if (specialHoursForm.startTime && specialHoursForm.endTime) {
        if (specialHoursForm.endTime <= specialHoursForm.startTime) {
          errors.endTime = "End time must be after start time";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle add time off
  const handleAddTimeOff = async () => {
    if (!validateTimeOffForm()) return;

    try {
      await dispatch(
        addTimeOffThunk({
          date: timeOffForm.date,
          type: timeOffForm.type._id,
          reason: timeOffForm.reason,
          isFullDay: timeOffForm.isFullDay,
          startTime: timeOffForm.isFullDay ? undefined : timeOffForm.startTime,
          endTime: timeOffForm.isFullDay ? undefined : timeOffForm.endTime,
        })
      ).unwrap();

      setIsAddModalOpen(false);
      resetTimeOffForm();
    } catch (error) {
      console.error("Failed to add time off:", error);
    }
  };

  // Handle remove time off
  const handleRemoveTimeOff = async (timeOffId) => {
    if (window.confirm("Are you sure you want to remove this time off?")) {
      try {
        await dispatch(removeTimeOffThunk(timeOffId)).unwrap();
      } catch (error) {
        console.error("Failed to remove time off:", error);
      }
    }
  };

  // Handle add special hours
  const handleAddSpecialHours = async () => {
    if (!validateSpecialHoursForm()) return;

    try {
      await dispatch(
        addSpecialHoursThunk({
          date: specialHoursForm.date,
          isAvailable: specialHoursForm.isAvailable,
          startTime: specialHoursForm.isAvailable
            ? specialHoursForm.startTime
            : undefined,
          endTime: specialHoursForm.isAvailable
            ? specialHoursForm.endTime
            : undefined,
          reason: specialHoursForm.reason,
        })
      ).unwrap();

      setIsSpecialHoursModalOpen(false);
      resetSpecialHoursForm();
    } catch (error) {
      console.error("Failed to add special hours:", error);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Check if date is in the past
  const isPastDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Get upcoming time off
  const getUpcomingTimeOff = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (timeOffList || [])
      .filter((timeOff) => new Date(timeOff.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Get past time off
  const getPastTimeOff = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (timeOffList || [])
      .filter((timeOff) => new Date(timeOff.date) < today)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Render time off card
  const renderTimeOffCard = (timeOff) => {
    const isPast = isPastDate(timeOff.date);

    return (
      <div
        key={timeOff._id}
        className={`bg-white border rounded-lg p-4 ${
          isPast ? "opacity-60" : ""
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <CalendarIcon className="w-5 h-5 text-gray-500" />
              <p className="font-semibold text-gray-900">
                {formatDate(timeOff.date)}
              </p>
              <Badge variant={getTypeColor(timeOff.type)}>
                {timeOffTypeOptions.find((t) => t._id === timeOff.type)?.name ||
                  timeOff.type}
              </Badge>
              {isPast && <Badge variant="default">Past</Badge>}
            </div>

            {timeOff.reason && (
              <p className="text-sm text-gray-600 mb-2">📝 {timeOff.reason}</p>
            )}

            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <ClockIcon className="w-4 h-4" />
                {timeOff.isFullDay
                  ? "Full Day"
                  : `${timeOff.startTime} - ${timeOff.endTime}`}
              </span>
            </div>
          </div>

          {!isPast && (
            <Button
              variant="danger-text"
              size="sm"
              onClick={() => handleRemoveTimeOff(timeOff._id)}
              disabled={loading}
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Render special hours card
  const renderSpecialHoursCard = (specialHours) => {
    const isPast = isPastDate(specialHours.date);

    return (
      <div
        key={specialHours._id}
        className={`bg-white border rounded-lg p-4 ${
          isPast ? "opacity-60" : ""
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <CalendarIcon className="w-5 h-5 text-gray-500" />
              <p className="font-semibold text-gray-900">
                {formatDate(specialHours.date)}
              </p>
              <Badge variant={specialHours.isAvailable ? "success" : "danger"}>
                {specialHours.isAvailable ? "Available" : "Not Available"}
              </Badge>
              {isPast && <Badge variant="default">Past</Badge>}
            </div>

            {specialHours.reason && (
              <p className="text-sm text-gray-600 mb-2">
                📝 {specialHours.reason}
              </p>
            )}

            {specialHours.isAvailable && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <ClockIcon className="w-4 h-4" />
                {specialHours.startTime} - {specialHours.endTime}
              </div>
            )}
          </div>

          {!isPast && (
            <Button
              variant="danger-text"
              size="sm"
              onClick={() => handleRemoveTimeOff(specialHours._id)}
              disabled={loading}
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  const upcomingTimeOff = getUpcomingTimeOff();
  const pastTimeOff = getPastTimeOff();

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Upcoming Time Off</p>
              <p className="text-2xl font-bold text-gray-900">
                {upcomingTimeOff.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center gap-3">
            <ClockIcon className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Special Hours</p>
              <p className="text-2xl font-bold text-gray-900">
                {(specialHoursList || []).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Past Entries</p>
              <p className="text-2xl font-bold text-gray-900">
                {pastTimeOff.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab("time-off")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "time-off"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Time Off
        </button>
        <button
          onClick={() => setActiveTab("special-hours")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "special-hours"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Special Hours
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "time-off" && (
        <div className="space-y-6">
          {/* Add Button */}
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-900">
              Time Off Requests
            </h3>
            <Button variant="primary" onClick={() => setIsAddModalOpen(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Request Time Off
            </Button>
          </div>

          {/* Upcoming Time Off */}
          {upcomingTimeOff.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Upcoming
              </h4>
              <div className="space-y-3">
                {upcomingTimeOff.map(renderTimeOffCard)}
              </div>
            </div>
          )}

          {/* Past Time Off */}
          {pastTimeOff.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Past Time Off
              </h4>
              <div className="space-y-3">
                {pastTimeOff.slice(0, 5).map(renderTimeOffCard)}
              </div>
              {pastTimeOff.length > 5 && (
                <p className="text-sm text-gray-600 text-center mt-3">
                  + {pastTimeOff.length - 5} more past entries
                </p>
              )}
            </div>
          )}

          {/* Empty State */}
          {upcomingTimeOff.length === 0 && pastTimeOff.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No time off scheduled</p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={() => setIsAddModalOpen(true)}
              >
                Request Your First Time Off
              </Button>
            </div>
          )}
        </div>
      )}

      {activeTab === "special-hours" && (
        <div className="space-y-6">
          {/* Add Button */}
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-900">
              Special Hours Configuration
            </h3>
            <Button
              variant="primary"
              onClick={() => setIsSpecialHoursModalOpen(true)}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Special Hours
            </Button>
          </div>

          {/* Special Hours List */}
          {(specialHoursList || []).length > 0 ? (
            <div className="space-y-3">
              {specialHoursList.map(renderSpecialHoursCard)}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No special hours configured</p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={() => setIsSpecialHoursModalOpen(true)}
              >
                Add Special Hours
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">About Time Off & Special Hours</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                <strong>Time Off:</strong> Days you won't be available for
                appointments
              </li>
              <li>
                <strong>Special Hours:</strong> Days with different working
                hours than usual
              </li>
              <li>
                Customers won't be able to book appointments during your time
                off
              </li>
              <li>Your status will auto-update based on these settings</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Time Off Modal */}
      <Modal
        isOpen={isAddModalOpen}
        closeModal={() => {
          setIsAddModalOpen(false);
          resetTimeOffForm();
        }}
        title="Request Time Off"
      >
        <div className="space-y-4">
          <Input
            label="Date"
            type="date"
            value={timeOffForm.date}
            onChange={(e) =>
              setTimeOffForm({ ...timeOffForm, date: e.target.value })
            }
            error={formErrors.date}
            min={new Date().toISOString().split("T")[0]}
            required
          />

          <Dropdown
            label="Type"
            options={timeOffTypeOptions}
            selected={timeOffForm.type}
            onChange={(type) => setTimeOffForm({ ...timeOffForm, type })}
            error={formErrors.type}
            placeholder="Select time off type"
            required
            clearable
          />

          <Input
            label="Reason (Optional)"
            type="text"
            value={timeOffForm.reason}
            onChange={(e) =>
              setTimeOffForm({ ...timeOffForm, reason: e.target.value })
            }
            placeholder="e.g., Family vacation, Doctor appointment"
            maxLength={100}
          />

          <div className="flex items-center gap-2">
            <Checkbox
              checked={timeOffForm.isFullDay}
              onChange={(e) =>
                setTimeOffForm({ ...timeOffForm, isFullDay: e.target.checked })
              }
            />
            <label className="text-sm font-medium text-gray-700">
              Full Day
            </label>
          </div>

          {!timeOffForm.isFullDay && (
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Start Time"
                type="time"
                value={timeOffForm.startTime}
                onChange={(e) =>
                  setTimeOffForm({ ...timeOffForm, startTime: e.target.value })
                }
                error={formErrors.startTime}
                required
              />
              <Input
                label="End Time"
                type="time"
                value={timeOffForm.endTime}
                onChange={(e) =>
                  setTimeOffForm({ ...timeOffForm, endTime: e.target.value })
                }
                error={formErrors.endTime}
                required
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsAddModalOpen(false);
                resetTimeOffForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddTimeOff}
              disabled={loading}
            >
              {loading ? "Adding..." : "Add Time Off"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Special Hours Modal */}
      <Modal
        isOpen={isSpecialHoursModalOpen}
        closeModal={() => {
          setIsSpecialHoursModalOpen(false);
          resetSpecialHoursForm();
        }}
        title="Add Special Hours"
      >
        <div className="space-y-4">
          <Input
            label="Date"
            type="date"
            value={specialHoursForm.date}
            onChange={(e) =>
              setSpecialHoursForm({ ...specialHoursForm, date: e.target.value })
            }
            error={formErrors.date}
            min={new Date().toISOString().split("T")[0]}
            required
          />

          <div className="flex items-center gap-2">
            <Checkbox
              checked={specialHoursForm.isAvailable}
              onChange={(e) =>
                setSpecialHoursForm({
                  ...specialHoursForm,
                  isAvailable: e.target.checked,
                })
              }
            />
            <label className="text-sm font-medium text-gray-700">
              Available on this day
            </label>
          </div>

          {specialHoursForm.isAvailable && (
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Start Time"
                type="time"
                value={specialHoursForm.startTime}
                onChange={(e) =>
                  setSpecialHoursForm({
                    ...specialHoursForm,
                    startTime: e.target.value,
                  })
                }
                error={formErrors.startTime}
                required
              />
              <Input
                label="End Time"
                type="time"
                value={specialHoursForm.endTime}
                onChange={(e) =>
                  setSpecialHoursForm({
                    ...specialHoursForm,
                    endTime: e.target.value,
                  })
                }
                error={formErrors.endTime}
                required
              />
            </div>
          )}

          <Input
            label="Reason (Optional)"
            type="text"
            value={specialHoursForm.reason}
            onChange={(e) =>
              setSpecialHoursForm({
                ...specialHoursForm,
                reason: e.target.value,
              })
            }
            placeholder="e.g., Early morning VIP appointments, Holiday hours"
            maxLength={100}
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsSpecialHoursModalOpen(false);
                resetSpecialHoursForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddSpecialHours}
              disabled={loading}
            >
              {loading ? "Adding..." : "Add Special Hours"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TimeOffCalendar;
